import {
  Children,
  forwardRef,
  isValidElement,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Surface, type SurfaceProps } from '@/components/ui/surface';
import { cn } from '@/lib/cn';

/**
 * Section — the canonical "panel" used inside every page.
 *
 *   <Section>
 *     <SectionHeader>
 *       <SectionTitle>Recent activity</SectionTitle>
 *       <SectionDescription>Last 5 audits.</SectionDescription>
 *       <SectionToolbar>{actions}</SectionToolbar>
 *     </SectionHeader>
 *     <SectionBody>…</SectionBody>
 *   </Section>
 *
 * SectionToolbar is pulled to the right of the heading row; everything else
 * stays on the left (title + description).
 */
export interface SectionProps extends SurfaceProps {
  /** Add bottom padding to the section. Defaults to true. */
  padded?: boolean;
}

export const Section = forwardRef<HTMLDivElement, SectionProps>(
  ({ className, padded = true, tone, ...props }, ref) => (
    <Surface
      ref={ref}
      tone={tone}
      className={cn('overflow-hidden', padded && 'p-5 sm:p-6', className)}
      {...props}
    />
  ),
);
Section.displayName = 'Section';

function isSectionToolbar(child: ReactNode): boolean {
  return (
    isValidElement(child) &&
    typeof child.type !== 'string' &&
    (child.type as { displayName?: string }).displayName === 'SectionToolbar'
  );
}

export function SectionHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  const childArray = Children.toArray(children);
  const toolbar = childArray.find(isSectionToolbar);
  const main = childArray.filter((c) => !isSectionToolbar(c));

  return (
    <div
      className={cn(
        'mb-4 flex flex-wrap items-start justify-between gap-3 sm:flex-nowrap',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1 space-y-1">{main}</div>
      {toolbar ?? null}
    </div>
  );
}

export function SectionTitle({
  as: Comp = 'h2',
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement> & {
  as?: 'h2' | 'h3' | 'h4';
}): JSX.Element {
  return (
    <Comp
      className={cn(
        'text-base font-semibold leading-tight text-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function SectionDescription({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLParagraphElement>): JSX.Element {
  return (
    <p className={cn('text-xs text-muted-foreground', className)} {...props}>
      {children}
    </p>
  );
}

export function SectionToolbar({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className={cn('flex shrink-0 items-center gap-2 self-start', className)}>
      {children}
    </div>
  );
}
SectionToolbar.displayName = 'SectionToolbar';

export function SectionBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn('space-y-3', className)} {...props} />;
}

/**
 * Compact section header for nested panels — used in sticky toolbars/filters.
 */
export function SectionDivider({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground',
        className,
      )}
      {...props}
    >
      <span>{children}</span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  );
}
